import { useEffect, useRef, useState } from "react";
import { Gauge, TrendingUp, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { SeoHead, homeJsonLd } from "@/lib/seo";
import InputPanel from "@/components/InputPanel";
import EvaluationSheet from "@/components/EvaluationSheet";
import ExamplesGallery from "@/components/ExamplesGallery";
import RelatedBlog from "@/components/RelatedBlog";

/**
 * Home page — single-purpose analysis launcher.
 *
 * Iteration 24 refreshes the hero so the input panel is the unambiguous
 * protagonist of the page now that map / feed / digest are out:
 *  - Bigger headline (up to text-8xl on wide viewports).
 *  - Replaced the three brand-stat blocks (Salento / LE-BR-TA / Engine)
 *    with a "what you'll get back" 3-icon strip pointing at the three
 *    headline outputs the report produces (deal score · net yield · risks).
 *  - InputPanel itself gets an olive accent bar + scarcity eyebrow.
 *
 * Layout rules:
 *  - lg+: 7/5 split, panel pinned to the bottom of the column so its base
 *         aligns with the bottom of the headline block.
 *  - mobile / tablet: stack with the panel right below the headline so a
 *    visitor who lands on phone immediately sees the input field.
 */
const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c63cbdc8-0615-477a-909e-bf93c5bd629a/images/22d791b30faf0b5c444097073c1211cb35bc72587e1f7f72a87e58442c3e48d4.png";

const Home = ({ onOpenPaywall }) => {
  const { t } = useLang();
  const [result, setResult] = useState(null);
  const sheetRef = useRef(null);

  // Smooth-scroll to the freshly-rendered sheet so the result is the very
  // next thing in the viewport without the user having to chase it.
  useEffect(() => {
    if (result && sheetRef.current) {
      sheetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const valueProps = [
    { icon: Gauge, label: t("hero.value1Label"), value: t("hero.value1Value") },
    { icon: TrendingUp, label: t("hero.value2Label"), value: t("hero.value2Value") },
    { icon: AlertTriangle, label: t("hero.value3Label"), value: t("hero.value3Value") },
  ];

  return (
    <main data-testid="home-page" className="relative bg-tufo overflow-hidden">
      <SeoHead
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        path="/"
        lang={t("seo.lang")}
        jsonLd={homeJsonLd()}
      />
      {/* Page-wide stone background — sits behind every section of the
          Home page (hero + gallery + result). Stops at the Footer because
          that lives outside this <main>. Desaturated so foreground stays focus. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          // `fixed` attaches the bg to the viewport instead of the element,
          // so even on a tall Home (hero + gallery + result) the stones
          // are always visible without any blank tufo gap. `cover` ensures
          // we never see edges of the image, regardless of viewport ratio.
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          filter: "grayscale(100%)",
        }}
      />
      {/* Olive accent strip at the very top of the Home page. */}
      <div aria-hidden className="absolute top-0 inset-x-0 h-[3px] bg-[#4A5D23] pointer-events-none" />

      {/* HERO */}
      <section className="relative">
        {/* Hero-bottom hairline divider between hero and examples gallery. */}
        <div aria-hidden className="absolute bottom-0 inset-x-0 h-px bg-[#E6E4DD]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            {/* Left — headline + value-prop strip */}
            <div className="lg:col-span-7">
              <div className="label-eyebrow inline-flex items-center gap-2" data-testid="hero-eyebrow">
                <span className="inline-block h-1.5 w-1.5 bg-[#4A5D23]" />
                {t("hero.eyebrow")}
              </div>
              <h1
                className="font-serif-display mt-6 leading-[0.95] tracking-tight text-[#282C2A]
                           text-4xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
              >
                {t("hero.title")}
              </h1>
              <p className="mt-7 text-base md:text-lg lg:text-xl text-[#3F4543] max-w-2xl leading-relaxed">
                {t("hero.sub")}
              </p>

              {/* What you'll get — replaces the old stats row. Each tile points
                  at one of the three headline outputs the report produces. */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                {valueProps.map((v) => (
                  <div
                    key={v.label}
                    data-testid="hero-value-prop"
                    className="bg-white/60 backdrop-blur-sm border border-[#E6E4DD] px-4 py-3 flex items-start gap-3"
                  >
                    <div className="bg-[#4A5D23] text-white p-1.5 mt-0.5">
                      <v.icon size={13} strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="label-eyebrow">{v.label}</div>
                      <div className="font-mono text-xs text-[#282C2A] mt-1">{v.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — the command panel */}
            <div className="lg:col-span-5">
              <div className="label-eyebrow inline-flex items-center gap-2 mb-3 text-[#4A5D23]">
                <span className="inline-block h-1.5 w-1.5 bg-[#4A5D23]" />
                {t("hero.panelEyebrow")}
              </div>
              <InputPanel onResult={setResult} onOpenPaywall={onOpenPaywall} />
              <p className="mt-4 text-xs text-[#6C706D] leading-relaxed">
                {t("hero.panelFooter")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXAMPLES — pre-analysed gallery so first-time visitors can preview
          the product without pasting anything. Auto-hides when the DB is dry. */}
      <div className="relative">
        <ExamplesGallery />
      </div>

      {/* TI PUÒ INTERESSARE — editorial blog teasers. Auto-hides on empty. */}
      <RelatedBlog />

      {/* RESULT */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-16" ref={sheetRef}>
        {result && <EvaluationSheet a={result} onOpenPaywall={onOpenPaywall} />}
      </section>
    </main>
  );
};

export default Home;
